// agentModeFocus.js — the browser focus follows the model's focus
// (WS-AGENT-MODE-1).
//
// The shared runtime owns WHERE the person is: the sidebar, the content, the
// ASK AI bar, an overlay, a list filter, the chat composer. The browser has
// one focused node. Two rules keep them one:
//   - a frame field the model focuses takes the browser focus
//     (agentModeFocusField, from the field's own render / update);
//   - while the model focuses no field, the frame itself holds the focus, so
//     the keydown bridge keeps receiving keys (agentModeFocusFrame).
// A pointer into a field the model has not focused tells the model first
// (AgentModeField onFocus). The focus moves in a microtask: a field that
// was hidden a moment ago is shown by then (the framework applies `hide` in
// a microtask).

// Which field the snapshot focuses (its input node's `target`), or null.
export const agentModeFocusTarget = (snap) => {
  if (!snap || typeof snap !== 'object') return null
  const overlays = Array.isArray(snap.overlays) ? snap.overlays : []
  const top = overlays[overlays.length - 1]
  if (top) {
    if (top.kind === 'picker') return top.query && top.query.focused ? top.query.target : null
    if (top.kind === 'form') {
      const f = (Array.isArray(top.fields) ? top.fields : []).find((x) => x && x.focused)
      return f && f.type !== 'select' ? f.target : null
    }
    return null
  }
  const ask = snap.footer && snap.footer.input
  if (ask && ask.focused) return ask.target
  const view = (snap.content && snap.content.view) || {}
  if (view.filter && view.filter.focused) return view.filter.target
  if (view.composer && view.composer.focused) return view.composer.target
  return null
}

// A field whose row says `focused`: take the browser focus. A node that is
// not attached yet (the first render of a whole mount) or not shown yet (an
// overlay whose `hide` lifts after this microtask) cannot take it, so the
// focus is tried again on the next animation frames (a few, then it stops).
export const agentModeFocusField = (el, s) => {
  if (!el || !s || !s.focused) return
  const attempt = (left) => {
    const node = el.node
    if (!node || typeof node.focus !== 'function') return
    const retry = () => {
      if (left > 0 && typeof requestAnimationFrame === 'function')
        requestAnimationFrame(() => attempt(left - 1))
    }
    if (!node.isConnected) return retry()
    const doc = node.ownerDocument
    if (!doc || doc.activeElement !== node) node.focus({ preventScroll: true })
    if (doc && doc.activeElement !== node) return retry()
    // The model's caret (↑ in the ask bar walks the history; a send clears).
    // Only a caret the MODEL moved is applied: a render that leaves it where
    // the person typed (a spinner tick, a toast) keeps their selection.
    const scope = el.scope || {}
    const caret = Number.isInteger(s.caret) ? Math.min(s.caret, node.value.length) : null
    if (caret === null || scope.agentModeCaret === caret) return
    scope.agentModeCaret = caret
    if (typeof node.setSelectionRange === 'function' && node.selectionStart !== caret)
      node.setSelectionRange(caret, caret)
  }
  queueMicrotask(() => attempt(8))
}

// No field focused by the model: the frame holds the focus (keys keep coming).
export function agentModeFocusFrame(frame) {
  const el = frame || this
  const root = el && el.state && el.state.root
  if (!el || !root) return
  const snap = this.call('agentModeSnapshot', root)
  if (snap && this.call('agentModeFocusTarget', snap)) return
  // Same retry as agentModeFocusField: at the first render of a page load
  // the frame is not attached yet, and the keys stayed dead until a click.
  const attempt = (left) => {
    const node = el.node
    if (!node || typeof node.focus !== 'function') return
    const retry = () => {
      if (left > 0 && typeof requestAnimationFrame === 'function')
        requestAnimationFrame(() => attempt(left - 1))
    }
    if (!node.isConnected) return retry()
    const doc = node.ownerDocument
    const active = doc && doc.activeElement
    if (active === node) return
    // A control of the frame (a tab, a button) may keep a focus it took.
    if (active && node.contains(active)) {
      const tag = String(active.tagName || '').toLowerCase()
      if (tag !== 'input' && tag !== 'textarea') return
      // A field the model focuses by now keeps it (its own render moved it).
      const now = this.call('agentModeSnapshot', root)
      if (now && this.call('agentModeFocusTarget', now)) return
    }
    node.focus({ preventScroll: true })
    if (doc && doc.activeElement !== node) retry()
  }
  queueMicrotask(() => attempt(8))
}
